import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMagic, 
  faLightbulb,
  faExclamationTriangle,
  faClock,
  faThumbsUp,
  faStar,
  faBullhorn,
  faImage,
  faVideo,
  faHashtag,
  faBullseye,
  faRocket,
  faInfoCircle,
  faArrowRight,
  faFilter,
  faSearch,
  faBrain,
  faChartLine,
  faSpinner,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import { 
  faInstagram, 
  faFacebook, 
  faTwitter, 
  faLinkedin,
  faTiktok
} from '@fortawesome/free-brands-svg-icons';
import { useAIRecommendations } from '../../context/AIRecommendationsContext';
import { useAuth } from '../../context/AuthContext';
import { aiRecommendationsAPI, creditsAPI, analysisJobsAPI, businessAuthAPI } from '../../services/api';

const AIRecommendationsPage: React.FC = () => {
  // @ts-expect-error - react-i18next supports namespace parameter but TypeScript types don't reflect it
  const { t, i18n } = useTranslation('dashboard');
  const { state, fetchRecommendations, setRecommendationDetail } = useAIRecommendations();
  const { business, refreshBusinessProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || 'tr';
  const resolveSection = (pathname: string) => {
    // Remove language prefix for section detection
    const pathWithoutLang = pathname.replace(/^\/(tr|en)/, '');
    if (pathWithoutLang.startsWith('/web-site')) return 'web-site';
    if (pathWithoutLang.startsWith('/ads')) return 'ads';
    return 'social-media';
  };
  const section = resolveSection(location.pathname);
  const recommendationsBasePath = `/${currentLang}/${section}/ai-recommendations`;
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Check analysis status from business model
  const analysisStatus = business?.analysisStatus?.['ai-recommendations']?.status || 'noanalyze';
  const isAnalysisPending = analysisStatus === 'pending';
  const isAnalysisCompleted = analysisStatus === 'completed';
  const isAnalysisFailed = analysisStatus === 'failed';
  const hasAnyAnalysis = isAnalysisCompleted;
  
  // Analysis start flow states
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [estimatedCredits, setEstimatedCredits] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Social media username setup states
  const [platforms, setPlatforms] = useState<Record<string, { username: string; loading: boolean; profile: any; error: string | null; verified: boolean; lastVerifiedUsername?: string | null }>>({
    facebook: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
    instagram: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
    twitter: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
    tiktok: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
    linkedin: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
  });
  const [verifying, setVerifying] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [setupError, setSetupError] = useState<string | null>(null);

  // Check if user has any social media accounts
  const hasAnySocialMediaAccount = React.useMemo(() => {
    if (!business) return false;
    const platformHasData = (p?: { username?: string; isConnected?: boolean }) =>
      !!(p?.username && p.username.trim()) || p?.isConnected === true;
    return platformHasData(business.facebook) || 
           platformHasData(business.instagram) || 
           platformHasData(business.twitter) || 
           platformHasData(business.tiktok) || 
           platformHasData(business.linkedin);
  }, [business]);

  // Check sync status for all connected social media accounts
  const syncStatuses = React.useMemo(() => {
    if (!business) return { hasAnyPending: false, allCompleted: false, hasAnyAccount: false };
    
    const accounts = [
      business.facebook,
      business.instagram,
      business.twitter,
      business.tiktok,
      business.linkedin
    ];
    
    const connectedAccounts = accounts.filter(acc => 
      acc && (!!(acc.username && acc.username.trim()) || acc.isConnected === true)
    );
    
    if (connectedAccounts.length === 0) {
      return { hasAnyPending: false, allCompleted: false, hasAnyAccount: false };
    }
    
    const hasAnyPending = connectedAccounts.some(acc => acc && acc.syncStatus === 'pending');
    const allCompleted = connectedAccounts.every(acc => acc && acc.syncStatus === 'completed');
    
    return { hasAnyPending, allCompleted, hasAnyAccount: true };
  }, [business]);

  // Handle username change
  const handleUsernameChange = (platform: string, value: string) => {
    setPlatforms(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        username: value,
        profile: value.trim() === prev[platform].lastVerifiedUsername ? prev[platform].profile : null,
        error: null,
        verified: value.trim() === prev[platform].lastVerifiedUsername ? prev[platform].verified : false,
      }
    }));
    setSetupError(null);
  };

  // Handle verify all
  const handleVerifyAll = async () => {
    const payload: Record<string, string> = {};
    Object.entries(platforms).forEach(([platform, state]) => {
      const trimmed = state.username.trim();
      if (trimmed && trimmed !== state.lastVerifiedUsername) {
        payload[platform] = trimmed;
      }
    });

    if (Object.keys(payload).length === 0) {
      return;
    }

    setVerifying(true);
    setPlatforms(prev => {
      const next = { ...prev };
      Object.keys(payload).forEach((platform) => {
        next[platform] = {
          ...next[platform],
          loading: true,
          error: null,
        };
      });
      return next;
    });
    setSetupError(null);

    try {
      const response = await businessAuthAPI.getSocialMediaProfiles(payload);
      const respData = response?.data?.data || {};
      const respErrors = (response?.data as any)?.errors || {};

      setPlatforms(prev => {
        const next = { ...prev };
        Object.keys(payload).forEach((platform) => {
          const platformResp = (respData as any)[platform] || null;
          if (platformResp) {
            next[platform] = {
              ...next[platform],
              loading: false,
              profile: platformResp,
              verified: true,
              error: null,
              lastVerifiedUsername: next[platform].username.trim(),
            };
          } else {
            const errorMsg = (respErrors as any)?.[platform] || t('socialMedia.aiRecommendations.profileNotFound');
            next[platform] = {
              ...next[platform],
              loading: false,
              profile: null,
              verified: false,
              error: errorMsg,
            };
          }
        });
        return next;
      });
    } catch (err: any) {
      const genericError = err?.response?.data?.message || t('socialMedia.aiRecommendations.verificationFailed');
      setPlatforms(prev => {
        const next = { ...prev };
        Object.keys(payload).forEach((platform) => {
          next[platform] = {
            ...next[platform],
            loading: false,
            profile: null,
            verified: false,
            error: err?.response?.data?.errors?.[platform] || genericError,
          };
        });
        return next;
      });
    } finally {
      setVerifying(false);
    }
  };

  // Handle submit
  const handleSubmit = async () => {
    const hasAnyUsername = Object.values(platforms).some(p => p.username.trim());
    if (!hasAnyUsername) {
      setSetupError(t('socialMedia.aiRecommendations.enterAtLeastOneUsername'));
          return;
        }
        
    const hasAnyVerified = Object.values(platforms).some(p => p.verified);
    if (!hasAnyVerified) {
      setSetupError(t('socialMedia.aiRecommendations.verifyAtLeastOneAccount'));
          return;
        }
        
    setSubmitting(true);
    setSetupError(null);

    try {
      const payload: any = {};
      Object.entries(platforms).forEach(([platform, state]) => {
        if (state.verified && state.username.trim()) {
          payload[platform] = state.username.trim();
        }
      });

      await businessAuthAPI.addSocialMediaAccounts(payload);
      await refreshBusinessProfile();
      
      try {
        void businessAuthAPI.syncSocialMediaData();
      } catch (_e) {
        // Ignore sync errors
      }
    } catch (err: any) {
      setSetupError(err.response?.data?.message || t('socialMedia.aiRecommendations.accountSaveError'));
    } finally {
      setSubmitting(false);
    }
  };

  const getProxyImageUrl = (url?: string) => {
    if (!url) return '';
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    return `${baseUrl}/proxy-image?url=${encodeURIComponent(url)}`;
  };

  const platformConfig = {
    facebook: { icon: faFacebook, label: t('socialMedia.analytics.platforms.facebook'), placeholder: t('socialMedia.analytics.platformPlaceholders.facebook') },
    instagram: { icon: faInstagram, label: t('socialMedia.analytics.platforms.instagram'), placeholder: t('socialMedia.analytics.platformPlaceholders.instagram') },
    twitter: { icon: faTwitter, label: t('socialMedia.analytics.platforms.twitter'), placeholder: t('socialMedia.analytics.platformPlaceholders.twitter') },
    tiktok: { icon: faTiktok, label: t('socialMedia.analytics.platforms.tiktok'), placeholder: t('socialMedia.analytics.platformPlaceholders.tiktok') },
    linkedin: { icon: faLinkedin, label: t('socialMedia.analytics.platforms.linkedin'), placeholder: t('socialMedia.analytics.platformPlaceholders.linkedin') },
  };

  // Fetch data on component mount - only if all syncs completed and analysis exists
  useEffect(() => {
    if (syncStatuses.allCompleted && isAnalysisCompleted && business?._id) {
      fetchRecommendations();
    }
  }, [syncStatuses.allCompleted, isAnalysisCompleted, business?._id, fetchRecommendations]);

  // Data from context
  const recommendationsData = state.recommendations;

  // Icon mapping function
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'faVideo': return faVideo;
      case 'faBullhorn': return faBullhorn;
      case 'faHashtag': return faHashtag;
      case 'faImage': return faImage;
      case 'faClock': return faClock;
      case 'faThumbsUp': return faThumbsUp;
      case 'faStar': return faStar;
      case 'faRocket': return faRocket;
      case 'faBullseye': return faBullseye;
      default: return faLightbulb;
    }
  };

  // Platform icon mapping
  const getPlatformIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'instagram':
        return faInstagram;
      case 'facebook':
        return faFacebook;
      case 'twitter':
        return faTwitter;
      case 'linkedin':
        return faLinkedin;
      case 'tiktok':
        return faTiktok;
      default:
        return faBullhorn;
    }
  };

  // Priority color mapping
  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'yüksek':
        return 'text-red-400 bg-red-500/10 border-red-500/20';
      case 'orta':
        return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20';
      case 'düşük':
        return 'text-green-400 bg-green-500/10 border-green-500/20';
      default:
        return 'text-blue-400 bg-blue-500/10 border-blue-500/20';
    }
  };

  // Effort color mapping
  const getEffortColor = (effort: string) => {
    switch (effort.toLowerCase()) {
      case 'yüksek':
        return 'text-red-400 bg-red-500/10 border-red-500/20';
      case 'orta':
        return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20';
      case 'düşük':
        return 'text-green-400 bg-green-500/10 border-green-500/20';
      default:
        return 'text-blue-400 bg-blue-500/10 border-blue-500/20';
    }
  };

  // Tüm recommendation'ları tek bir listede topla (filtrelemeden önce)
  const getAllRecommendations = () => {
    if (!recommendationsData) return [];
    
    let allRecommendations: any[] = [];
    
    // Collect all recommendations from all categories with category info
    Object.entries(recommendationsData).forEach(([categoryKey, categoryData]: [string, any]) => {
      if (categoryData.recommendations) {
        categoryData.recommendations.forEach((rec: any) => {
          allRecommendations.push({ ...rec, _categoryKey: categoryKey });
        });
      }
      // Also include basic recommendations
      if (categoryData.basic) {
        categoryData.basic.forEach((rec: any) => {
          allRecommendations.push({ ...rec, _categoryKey: categoryKey });
        });
      }
    });

    return allRecommendations;
  };

  // Tüm recommendation'ları al (filtrelemeden önce)
  const allRecommendations = getAllRecommendations();

  // Filter recommendations including basic ones
  const filteredRecommendations = () => {
    return allRecommendations.filter(rec => {
      const matchesCategory = selectedCategory === 'all' || rec.category?.toLowerCase().includes(selectedCategory.toLowerCase());
      const matchesPriority = selectedPriority === 'all' || rec.priority?.toLowerCase() === selectedPriority.toLowerCase();
      const matchesSearch = searchTerm === '' || 
        rec.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rec.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rec.platform?.toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesCategory && matchesPriority && matchesSearch;
    });
  };

  // Handle detail button click - recommendation objesini direkt state'e kaydet ve index kullan
  const handleDetailClick = (recommendation: any, index: number) => {
    // Recommendation'ı state'e kaydet
    setRecommendationDetail(recommendation);
    // Index'i URL'e ekle
    navigate(`${recommendationsBasePath}/${index}`);
  };

  // Show social media username setup if no accounts exist
  if (!hasAnySocialMediaAccount) {
    const hasAnyUsername = Object.values(platforms).some(p => p.username.trim());
    const allEnteredAreVerified = Object.values(platforms).every(p => !p.username.trim() || p.verified);
    const primaryCtaIsContinue = hasAnyUsername && allEnteredAreVerified && !verifying;

    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="w-full max-w-2xl space-y-6">
          <div className="text-center">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-6 mx-auto">
              <FontAwesomeIcon icon={faMagic} className="text-3xl sm:text-4xl text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-4">{t('socialMedia.aiRecommendations.connectAccounts')}</h1>
            <p className="text-white/70 text-sm sm:text-base mb-8">
              {t('socialMedia.aiRecommendations.connectAccountsDescription')}
            </p>
          </div>

          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-4 md:p-6 space-y-6">
            {setupError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
                {setupError}
              </div>
            )}

            <div className="space-y-4">
              {Object.entries(platformConfig).map(([platform, config]) => (
                <div key={platform} className="space-y-2">
                  <label className="flex items-center space-x-2 text-white/80 text-sm">
                    <FontAwesomeIcon icon={config.icon} className="text-lg" />
                    <span>{config.label}</span>
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      placeholder={config.placeholder}
                      value={platforms[platform].username}
                      onChange={(e) => handleUsernameChange(platform, e.target.value)}
                      className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-purple-400"
                    />
                    {platforms[platform].verified && (
                      <FontAwesomeIcon icon={faCheck} className="text-green-400" />
                    )}
                    {platforms[platform].loading && (
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin text-white/50" />
                    )}
                  </div>
                  {platforms[platform].error && (
                    <p className="text-red-400 text-xs">{platforms[platform].error}</p>
                  )}
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleVerifyAll}
                disabled={verifying || !hasAnyUsername}
                className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {verifying ? (
                  <span className="flex items-center justify-center">
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                    {t('socialMedia.aiRecommendations.verifying')}
                  </span>
                ) : (
                  t('socialMedia.aiRecommendations.verifyAccounts')
                )}
              </button>
              <button
                onClick={handleSubmit}
                disabled={!primaryCtaIsContinue || submitting}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-blue-600 rounded-lg text-white font-semibold hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <span className="flex items-center justify-center">
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                    {t('socialMedia.aiRecommendations.adding')}
                  </span>
                ) : (
                  t('socialMedia.aiRecommendations.addAccounts')
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show sync pending screen if any account is syncing
  if (syncStatuses.hasAnyAccount && syncStatuses.hasAnyPending) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-6 mx-auto">
            <div className="w-8 h-8 sm:w-10 sm:h-10 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
          </div>
          <FontAwesomeIcon icon={faMagic} className="text-4xl sm:text-5xl text-purple-500 mb-4" />
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3">{t('socialMedia.aiRecommendations.dataSyncing')}</h1>
          <p className="text-white/70 text-sm sm:text-base mb-6">
            {t('socialMedia.aiRecommendations.dataSyncingDescription')}
          </p>
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-4">
            <p className="text-white/60 text-xs sm:text-sm">
              {t('socialMedia.aiRecommendations.syncCompleteMessage')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show analysis start screen if all syncs completed but no analysis exists
  if (syncStatuses.allCompleted && !isAnalysisCompleted && !isAnalysisPending) {
    return (
      <>
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="w-full max-w-md space-y-6">
            <div className="text-center">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-6 mx-auto">
                <FontAwesomeIcon icon={faBrain} className="text-3xl sm:text-4xl text-white" />
              </div>
              <FontAwesomeIcon icon={faMagic} className="text-4xl sm:text-5xl text-purple-500 mb-4" />
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3">{t('socialMedia.aiRecommendations.startAiAnalysis')}</h1>
              <p className="text-white/70 text-sm sm:text-base mb-6">
                {t('socialMedia.aiRecommendations.dataReady')}
              </p>
            </div>

            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-4 md:p-6 space-y-4">
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <FontAwesomeIcon icon={faLightbulb} className="text-yellow-400 mt-1" />
                  <div>
                    <p className="text-white font-medium text-sm sm:text-base">{t('socialMedia.aiRecommendations.aiAnalysisFeatures')}</p>
                    <p className="text-white/70 text-xs sm:text-sm">{t('socialMedia.common.detailedPerformance')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <FontAwesomeIcon icon={faChartLine} className="text-blue-400 mt-1" />
                  <div>
                    <p className="text-white font-medium text-sm sm:text-base">{t('socialMedia.aiRecommendations.chartsAndReports')}</p>
                    <p className="text-white/70 text-xs sm:text-sm">{t('socialMedia.aiRecommendations.chartsAndReportsDescription')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <FontAwesomeIcon icon={faBrain} className="text-purple-400 mt-1" />
                  <div>
                    <p className="text-white font-medium text-sm sm:text-base">{t('socialMedia.aiRecommendations.smartRecommendations')}</p>
                    <p className="text-white/70 text-xs sm:text-sm">{t('socialMedia.aiRecommendations.smartRecommendationsDescription')}</p>
                  </div>
                </div>
              </div>

              <button
                onClick={async () => {
                  setAiModalOpen(true);
                  setEstimatedCredits(null);
                  // Set default dates (last 30 days)
                  const today = new Date();
                  const thirtyDaysAgo = new Date();
                  thirtyDaysAgo.setDate(today.getDate() - 30);
                  setEndDate(today.toISOString().split('T')[0]);
                  setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
                }}
                className="w-full bg-gradient-to-r from-purple-500 to-blue-600 px-6 py-4 rounded-xl text-white font-semibold hover:opacity-90 transition-opacity text-base sm:text-lg"
              >
                <FontAwesomeIcon icon={faBrain} className="mr-2" />
                {t('socialMedia.aiRecommendations.startAiAnalysis')}
              </button>
            </div>
          </div>
        </div>

        {/* AI Analysis Modal */}
        {aiModalOpen && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setAiModalOpen(false)}></div>
            <div className="absolute top-4 left-4 right-4 bottom-4 sm:top-10 sm:left-10 sm:right-10 sm:bottom-10 md:top-1/2 md:left-1/2 md:right-auto md:bottom-auto md:-translate-x-1/2 md:-translate-y-1/2 bg-gray-900 rounded-2xl border border-white/10 w-full max-w-md overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-white/10">
                <h3 className="text-lg sm:text-xl font-bold text-white">{t('socialMedia.aiRecommendations.modal.aiAnalysisTitle')}</h3>
              </div>
              <div className="p-4 sm:p-6 space-y-4">
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">{t('socialMedia.aiRecommendations.modal.startDate') || 'Başlangıç Tarihi'}</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      max={endDate || new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">{t('socialMedia.aiRecommendations.modal.endDate') || 'Bitiş Tarihi'}</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      min={startDate}
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                    />
                  </div>
                </div>
                <div className="text-white/80">
                  {t('socialMedia.aiRecommendations.modal.estimatedCredits')}
                  <div className="mt-2 text-xl sm:text-2xl font-semibold text-white">
                    {estimateLoading ? (
                      <span className="flex items-center">
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                        {t('socialMedia.aiRecommendations.modal.calculating')}
                      </span>
                    ) : (
                      estimatedCredits !== null 
                        ? Number(estimatedCredits).toLocaleString(i18n.language === 'tr' ? 'tr-TR' : 'en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                        : '—'
                    )}
                  </div>
                </div>
                <button
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-700 py-2 px-4 rounded-xl text-white font-medium hover:from-blue-600 hover:to-blue-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!startDate || !endDate}
                  onClick={async () => {
                    if (!startDate || !endDate) return;
                    setEstimateLoading(true);
                    setEstimatedCredits(null);
                    try {
                      const res = await creditsAPI.estimate('airecommendations', startDate, endDate);
                      const value = res?.data?.estimatedCredits ?? res?.data?.data?.estimatedCredits ?? null;
                      setEstimatedCredits(typeof value === 'number' ? value : null);
                    } finally {
                      setEstimateLoading(false);
                    }
                  }}
                >
                  {t('socialMedia.aiRecommendations.modal.calculateCredits') || 'Kredi Hesapla'}
                </button>
                <button
                  className="w-full bg-gradient-to-r from-purple-500 to-purple-700 py-3 px-6 rounded-xl text-white font-semibold hover:from-purple-600 hover:to-purple-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={estimateLoading || estimatedCredits === null || !startDate || !endDate}
                  onClick={async () => {
                    try {
                      if (!startDate || !endDate) return;
                      setEstimateLoading(true);
                      await analysisJobsAPI.start('airecommendations', startDate, endDate);
                    } finally {
                      setEstimateLoading(false);
                      setAiModalOpen(false);
                    }
                  }}
                >
                  {t('socialMedia.aiRecommendations.modal.spend')}
                </button>
                <button
                  className="w-full mt-2 bg-white/10 border border-white/20 py-3 px-6 rounded-xl text-white hover:bg-white/20 transition"
                  onClick={() => {
                    setAiModalOpen(false);
                    setStartDate('');
                    setEndDate('');
                    setEstimatedCredits(null);
                  }}
                >
                  {t('socialMedia.aiRecommendations.modal.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Show loading while analysis is pending (only if all syncs are completed)
  if (syncStatuses.allCompleted && isAnalysisPending) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-6 mx-auto">
            <div className="w-8 h-8 sm:w-10 sm:h-10 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
          </div>
          <FontAwesomeIcon icon={faMagic} className="text-4xl sm:text-5xl text-purple-500 mb-4" />
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3">{t('socialMedia.aiRecommendations.analysisInProgress')}</h1>
          <p className="text-white/70 text-sm sm:text-base mb-6">
            {t('socialMedia.aiRecommendations.analysisInProgressDescription')}
          </p>
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-4">
            <p className="text-white/60 text-xs sm:text-sm">
              {t('socialMedia.aiRecommendations.analysisCompleteMessage')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (state.loading.recommendations) {
  return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-4 mx-auto">
            <div className="w-8 h-8 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
        </div>
          <p className="text-white/70 text-lg font-medium">{t('socialMedia.aiRecommendations.loading')}</p>
          <p className="text-white/50 text-sm">{t('socialMedia.aiRecommendations.loadingDescription')}</p>
        </div>
      </div>
    );
  }

  if (state.error.recommendations === 'PREPARING') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-4 mx-auto">
            <div className="w-8 h-8 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
          </div>
          <p className="text-white/70 text-lg font-medium">{t('socialMedia.aiRecommendations.preparing')}</p>
          <p className="text-white/50 text-sm">{t('socialMedia.aiRecommendations.preparingDescription')}</p>
        </div>
          </div>
    );
  }

  if (state.error.recommendations) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-400 text-6xl mb-6" />
          <h1 className="text-3xl font-bold text-white mb-4">{t('socialMedia.common.errorOccurred')}</h1>
          <p className="text-white/70 mb-8 max-w-md">{state.error.recommendations}</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-gradient-to-r from-purple-500 to-blue-500 px-8 py-3 rounded-xl text-white font-medium hover:opacity-90 transition-opacity"
          >
            {t('socialMedia.common.retry')}
          </button>
        </div>
            </div>
    );
  }

  if (!recommendationsData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <FontAwesomeIcon icon={faInfoCircle} className="text-blue-400 text-6xl mb-6" />
          <h1 className="text-3xl font-bold text-white mb-4">{t('socialMedia.common.noRecommendations')}</h1>
          <p className="text-white/70 mb-8 max-w-md">{t('socialMedia.common.noRecommendationsDescription')}</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-gradient-to-r from-purple-500 to-blue-500 px-8 py-3 rounded-xl text-white font-medium hover:opacity-90 transition-opacity"
          >
            {t('socialMedia.common.refresh')}
          </button>
        </div>
            </div>
    );
  }

  const recommendations = filteredRecommendations();

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <FontAwesomeIcon icon={faMagic} className="text-4xl text-purple-400" />
          <div>
            <h1 className="text-3xl font-bold text-white">{t('socialMedia.aiRecommendations.title')}</h1>
            <p className="text-white/70">{t('socialMedia.aiRecommendations.subtitle')}</p>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={async () => {
              setAiModalOpen(true);
              setEstimatedCredits(null);
              // Set default dates (last 30 days)
              const today = new Date();
              const thirtyDaysAgo = new Date();
              thirtyDaysAgo.setDate(today.getDate() - 30);
              setEndDate(today.toISOString().split('T')[0]);
              setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
            }}
            className="px-3 md:px-4 py-2 rounded-xl bg-white/10 border border-white/20 text-white text-xs md:text-sm md:hover:bg-white/20 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="hidden sm:inline">{t('socialMedia.aiRecommendations.startAiAnalysisButton')}</span>
            <span className="sm:hidden">{t('socialMedia.aiRecommendations.aiAnalysis')}</span>
          </button>
          <span className="px-3 py-1 bg-purple-500/20 text-purple-400 text-sm rounded-full border border-purple-500/30">
            {recommendations.length} {t('socialMedia.aiRecommendations.recommendationCount')}
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <FontAwesomeIcon 
                icon={faSearch} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/50"
              />
              <input
                type="text"
                placeholder={t('socialMedia.aiRecommendations.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-purple-500/50 focus:bg-white/10"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div className="relative">
            <FontAwesomeIcon 
              icon={faFilter} 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/50"
            />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-lg text-white pl-10 pr-8 py-3 focus:outline-none focus:border-purple-500/50 min-w-[150px]"
            >
              <option value="all">{t('socialMedia.aiRecommendations.categories.all')}</option>
              <option value="büyüme">{t('socialMedia.aiRecommendations.categories.growth')}</option>
              <option value="etkileşim">{t('socialMedia.aiRecommendations.categories.engagement')}</option>
              <option value="içerik">{t('socialMedia.aiRecommendations.categories.content')}</option>
              <option value="optimizasyon">{t('socialMedia.aiRecommendations.categories.optimization')}</option>
            </select>
      </div>

          {/* Priority Filter */}
          <div className="relative">
            <FontAwesomeIcon 
              icon={faStar} 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/50"
            />
            <select
              value={selectedPriority}
              onChange={(e) => setSelectedPriority(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-lg text-white pl-10 pr-8 py-3 focus:outline-none focus:border-purple-500/50 min-w-[120px]"
            >
              <option value="all">{t('socialMedia.aiRecommendations.priorities.all')}</option>
              <option value="yüksek">{t('socialMedia.aiRecommendations.priorities.high')}</option>
              <option value="orta">{t('socialMedia.aiRecommendations.priorities.medium')}</option>
              <option value="düşük">{t('socialMedia.aiRecommendations.priorities.low')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Recommendations Grid */}
      {recommendations.length === 0 ? (
        <div className="text-center py-12">
          <FontAwesomeIcon icon={faSearch} className="text-white/30 text-4xl mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">{t('socialMedia.aiRecommendations.noRecommendationsFound')}</h3>
          <p className="text-white/50">{t('socialMedia.aiRecommendations.noRecommendationsFoundDescription')}</p>
                </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {recommendations.map((recommendation, filteredIndex) => {
            const isBasic = !recommendation.platform && !recommendation.category; // Basic recommendations don't have platform and category fields
            // Tüm recommendation listesinde bu recommendation'ın gerçek index'ini bul
            const realIndex = allRecommendations.findIndex(rec => 
              rec.title === recommendation.title && 
              rec.platform === recommendation.platform &&
              rec._categoryKey === recommendation._categoryKey
            );
            
            return (
              <div key={filteredIndex} className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors group">
              {/* Header */}
              <div className="flex items-start space-x-3 mb-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                  <FontAwesomeIcon icon={getIcon(recommendation.icon || 'faLightbulb')} className="text-white text-lg" />
                </div>
                <div className="flex-1 min-w-0">
                  {!isBasic && (
                    <div className="flex items-center space-x-2 mb-1">
                      <FontAwesomeIcon 
                        icon={getPlatformIcon(recommendation.platform)} 
                        className="text-blue-400 text-lg"
                      />
                      <span className="text-blue-400 font-medium text-sm">{recommendation.platform}</span>
                    </div>
                  )}
                  <h3 className="text-lg font-semibold text-white line-clamp-2 group-hover:text-purple-300 transition-colors">
                    {recommendation.title}
                  </h3>
                </div>
              </div>

              {/* Description */}
              <p className="text-white/70 text-sm mb-4 line-clamp-3">{recommendation.description}</p>

              {/* Tags - Only show for detailed recommendations */}
              {!isBasic && (
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(recommendation.priority)}`}>
                    {recommendation.priority} {t('socialMedia.aiRecommendations.priority')}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getEffortColor(recommendation.effort)}`}>
                    {recommendation.effort} {t('socialMedia.aiRecommendations.effort')}
                  </span>
                  <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full border border-blue-500/30">
                    {recommendation.category}
                  </span>
                </div>
              )}
              
                {/* Impact & Timeline - Only show for detailed recommendations */}
              {!isBasic && (
                <div className="space-y-2 mb-4">
                  <div className="flex items-center space-x-2">
                    <FontAwesomeIcon icon={faRocket} className="text-green-400 text-sm" />
                    <span className="text-white/70 text-sm">{t('socialMedia.aiRecommendations.impact')} {recommendation.impact}</span>
                </div>
                  <div className="flex items-center space-x-2">
                    <FontAwesomeIcon icon={faClock} className="text-blue-400 text-sm" />
                    <span className="text-white/70 text-sm">{t('socialMedia.aiRecommendations.timeline')} {recommendation.timeline}</span>
                  </div>
                </div>
              )}

            
              {/* Actions - Only show details button for detailed recommendations */}
              {!isBasic && (
                <div className="flex space-x-3">
                  <button
                    onClick={() => handleDetailClick(recommendation, realIndex)}
                    className="flex-1 bg-gradient-to-r from-purple-500 to-purple-700 text-white py-2 px-4 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity flex items-center justify-center space-x-2"
                  >
                    <FontAwesomeIcon icon={faInfoCircle} />
                    <span>{t('socialMedia.common.details')}</span>
                    <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                  </button>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {/* AI Analysis Modal */}
      {aiModalOpen && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setAiModalOpen(false)}></div>
          <div className="absolute top-4 left-4 right-4 bottom-4 sm:top-10 sm:left-10 sm:right-10 sm:bottom-10 md:top-1/2 md:left-1/2 md:right-auto md:bottom-auto md:-translate-x-1/2 md:-translate-y-1/2 bg-gray-900 rounded-2xl border border-white/10 w-full max-w-md overflow-hidden">
            <div className="p-4 sm:p-6 border-b border-white/10">
              <h3 className="text-lg sm:text-xl font-bold text-white">{t('socialMedia.aiRecommendations.modal.aiAnalysisTitle')}</h3>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">{t('socialMedia.aiRecommendations.modal.startDate') || 'Başlangıç Tarihi'}</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    max={endDate || new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">{t('socialMedia.aiRecommendations.modal.endDate') || 'Bitiş Tarihi'}</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                  />
                </div>
              </div>
              <div className="text-white/80">
                {t('socialMedia.aiRecommendations.modal.estimatedCredits')}
                <div className="mt-2 text-xl sm:text-2xl font-semibold text-white">
                  {estimateLoading ? (
                    <span className="flex items-center">
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                      {t('socialMedia.aiRecommendations.modal.calculating')}
                    </span>
                  ) : (
                    estimatedCredits !== null 
                      ? Number(estimatedCredits).toLocaleString(i18n.language === 'tr' ? 'tr-TR' : 'en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                      : '—'
                  )}
                </div>
              </div>
              <button
                className="w-full bg-gradient-to-r from-blue-500 to-blue-700 py-2 px-4 rounded-xl text-white font-medium hover:from-blue-600 hover:to-blue-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!startDate || !endDate}
                onClick={async () => {
                  if (!startDate || !endDate) return;
                  setEstimateLoading(true);
                  setEstimatedCredits(null);
                  try {
                    const res = await creditsAPI.estimate('airecommendations', startDate, endDate);
                    const value = res?.data?.estimatedCredits ?? res?.data?.data?.estimatedCredits ?? null;
                    setEstimatedCredits(typeof value === 'number' ? value : null);
                  } finally {
                    setEstimateLoading(false);
                  }
                }}
              >
                {t('socialMedia.aiRecommendations.modal.calculateCredits') || 'Kredi Hesapla'}
              </button>
              <button
                className="w-full bg-gradient-to-r from-purple-500 to-purple-700 py-3 px-6 rounded-xl text-white font-semibold hover:from-purple-600 hover:to-purple-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={estimateLoading || estimatedCredits === null || !startDate || !endDate}
                onClick={async () => {
                  try {
                    if (!startDate || !endDate) return;
                    setEstimateLoading(true);
                    await analysisJobsAPI.start('airecommendations', startDate, endDate);
                  } finally {
                    setEstimateLoading(false);
                    setAiModalOpen(false);
                  }
                }}
              >
                {t('socialMedia.aiRecommendations.modal.spend')}
              </button>
              <button
                className="w-full mt-2 bg-white/10 border border-white/20 py-3 px-6 rounded-xl text-white hover:bg-white/20 transition"
                onClick={() => {
                  setAiModalOpen(false);
                  setStartDate('');
                  setEndDate('');
                  setEstimatedCredits(null);
                }}
              >
                {t('socialMedia.aiRecommendations.modal.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIRecommendationsPage;